import React, { createContext, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Spinner,
  Text
} from '@efast_public/fjd-component-library';
import { ConfirmationModal } from '../../modals/ConfirmationModal/ConfirmationModal';

export interface AlertProps {
  intent: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  cloasable?: boolean;
}

/**
 * Call this function, when you want to ask for confirmation of the customer to trigger a certain request (=callback)
 * @param callback request callback which is getting triggerd, after customer confirm within the dialog
 * @param title overrides the default title, provided in <NotificationProvider ... />
 * @param confirmationButtonLabel label of the confirmation button
 * @param abortButtonLabel label of the abort button
 * @param content the content of the notification modal.
 */
export interface ModalProps {
  callback: () => unknown;
  title?: string;
  confirmationButtonLabel?: string;
  abortButtonLabel?: string;
  content?: React.ReactNode;
}

interface NotificationContextProps {
  confirmByUser: ({
    callback,
    title,
    confirmationButtonLabel,
    abortButtonLabel,
    content
  }: ModalProps) => void;
  setLoadingIndicators: React.Dispatch<React.SetStateAction<Array<string>>>;
  notifyUser: (props: AlertProps) => void;
}

export const NotificationContext = createContext<NotificationContextProps>({
  confirmByUser: () => {},
  setLoadingIndicators: () => {},
  notifyUser: () => {}
});

interface INotificationProviderProps {
  readonly children: React.ReactNode;
  readonly defaultModalConfirmationButtonLabel: string;
  readonly defaultModalAbortButtonLabel: string;
  readonly defaultModalHeading: string;
  readonly defaultModalContent: React.ReactNode;
  readonly defaultAlertErrorMessage: string;
  readonly defaultAlertSuccessMessage: string;
}

type NotifcationAlerts = AlertProps & { id?: string };

/**
 * Set the default label values for the global alert and modal. Example for 18next: modalHeading={t('modalHeading')}
 * @param children
 * @param defaultModalConfirmationButtonLabel label of the confirmation button of the confirmation modal
 * @param defaultModalAbortButtonLabel label of the abort button of the confirmation modal
 * @param defaultModalHeading heading of the confirmation modal
 * @param defaultModalContent content of the confirmation modal
 * @param defaultAlertErrorMessage error message shown in toast message
 * @param alertDefaultSuccessMessage success message shown in toast message
 * @constructor
 */
export function NotificationProvider({
  children,
  defaultModalConfirmationButtonLabel,
  defaultModalAbortButtonLabel,
  defaultModalHeading,
  defaultModalContent,
  defaultAlertErrorMessage,
  defaultAlertSuccessMessage
}: INotificationProviderProps) {
  const [alerts, setAlerts] = useState<NotifcationAlerts[]>([]);
  const [shouldUserConfirm, setShouldUserConfirm] = useState<null | ModalProps>(
    null
  );
  const [loadingIndicators, setLoadingIndicators] = useState<string[]>([]);

  function confirmByUser(props: ModalProps) {
    setShouldUserConfirm(props);
  }

  /**
   * Call this function, when an alert should appear after your request is settled.
   * @param status HTTP Status from the corresponding request. Necessary to determine the state of the response
   * @param message overrides the message from server
   */
  const notifyUser = useCallback(
    ({ intent, title }: AlertProps) => {
      const id = `${Math.round(Math.random() * 1000000)}`;

      if (intent === 'success')
        setAlerts((a) => [
          ...a,
          { id, intent, title: title ?? defaultAlertSuccessMessage }
        ]);
      if (intent === 'error')
        setAlerts((a) => [
          ...a,
          { id, intent, title: title ?? defaultAlertErrorMessage }
        ]);
      if (intent === 'info' || intent === 'warning')
        setAlerts((a) => [...a, { id, intent, title: title ?? '' }]);

      window.setTimeout(() => {
        setAlerts((currentAlerts) => currentAlerts.filter((a) => a.id !== id));
      }, 5000);
    },
    [defaultAlertErrorMessage, defaultAlertSuccessMessage]
  );

  const notificationContextApi = useMemo(
    () => ({
      confirmByUser,
      notifyUser,
      setLoadingIndicators
    }),
    [notifyUser]
  );

  return (
    <NotificationContext.Provider value={notificationContextApi}>
      {children}
      <div
        style={{
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          bottom: '1rem',
          left: '50%',
          width: '95vw',
          maxWidth: '24rem',
          transform: 'translate3d(-50%, 0, 0)',
          zIndex: 10000
        }}
      >
        {[...alerts].reverse().map((currentAlert) => (
          <Alert
            key={currentAlert.id}
            variant={currentAlert.intent}
            message={currentAlert.title}
          />
        ))}
      </div>
      {shouldUserConfirm !== null && (
        <ConfirmationModal
          isConfirmationModalVisible
          confirmationButtonLabel={
            shouldUserConfirm?.confirmationButtonLabel ??
            defaultModalConfirmationButtonLabel
          }
          abortButtonLabel={
            shouldUserConfirm?.abortButtonLabel ?? defaultModalAbortButtonLabel
          }
          content={shouldUserConfirm?.content ?? defaultModalContent}
          confirmationHeading={shouldUserConfirm?.title ?? defaultModalHeading}
          onConfirm={() => {
            shouldUserConfirm?.callback();
            setShouldUserConfirm(null);
          }}
          onAbort={() => setShouldUserConfirm(null)}
        />
      )}
      {loadingIndicators.length > 0 && (
        <Backdrop>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <Text size="xxl" strong>
              {loadingIndicators[0]}
            </Text>
            <Spinner />
          </div>
        </Backdrop>
      )}
    </NotificationContext.Provider>
  );
}
